import { useEffect, useState } from "react";

import { getSupabase, isSupabaseConfigured } from "./supabase";
import { useTenantId } from "./tenant";
import { scopeTenant } from "./tenantScope.mjs";

/**
 * MER-171: detect "no canonical data yet" for a tenant by probing for
 * the EXISTENCE of a service_event row. If none exists we render the
 * FirstRunOverlay over the dashboard so a fresh customer isn't greeted
 * by a sea of `n/a`.
 *
 * MER-503: this was a `count: exact, head: true` over the tenant's
 * FULL service_event set (~95k rows for DWS) to answer a binary
 * question. Under refresh-heavy load the count hung or 503'd
 * (MER-364 statement-timeout class) and the pending request kept
 * document_idle from ever firing, blocking every automated browser
 * pass. `select tenant_id limit 1` stops at the first row, and the
 * abort timeout guarantees the probe can never hang the page.
 *
 * Returned states:
 *   - `null` while the count is in-flight (don't flash the overlay
 *     before we know the answer)
 *   - `true` once the count is > 0
 *   - `false` once the count is exactly 0
 *
 * The probe runs once per slug mount. A re-check fires when the user
 * lands back on the dashboard (e.g. after uploading via the external
 * upload portal) through the storage event in `markOnboardingDismissed`.
 */
const PROBE_TIMEOUT_MS = 8000;

export function useHasCanonicalData(slug: string | undefined): boolean | null {
  const [hasData, setHasData] = useState<boolean | null>(null);
  const tenantId = useTenantId();

  useEffect(() => {
    if (!slug) {
      setHasData(null);
      return;
    }
    if (!isSupabaseConfigured()) {
      // Without Supabase we have no way to probe; treat as "has data"
      // so the dev / static-preview builds aren't hijacked by the overlay.
      setHasData(true);
      return;
    }
    // Fail-closed: wait for the slug to resolve to a tenant_id before
    // probing, so the count can't span tenants.
    if (!tenantId) {
      setHasData(null);
      return;
    }
    const boundTenantId = tenantId;

    let cancelled = false;
    setHasData(null);

    (async () => {
      try {
        const supabase = getSupabase();
        const { data, error } = await scopeTenant(
          supabase.from("service_event").select("tenant_id"),
          boundTenantId,
        )
          .limit(1)
          .abortSignal(AbortSignal.timeout(PROBE_TIMEOUT_MS));
        if (cancelled) return;
        if (error) {
          // Table missing, RLS blocked the read, or the probe timed
          // out: treat as "has data" rather than locking the user
          // behind an overlay they can't escape from. Loud in console,
          // silent in UI (MER-464 rule).
          console.warn(`useHasCanonicalData probe failed: ${error.message}`);
          setHasData(true);
          return;
        }
        setHasData(Array.isArray(data) && data.length > 0);
      } catch (err) {
        console.warn(
          `useHasCanonicalData probe failed: ${err instanceof Error ? err.message : String(err)}`,
        );
        if (!cancelled) setHasData(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug, tenantId]);

  return hasData;
}

const DISMISS_KEY_PREFIX = "meridian:onboarding-dismissed:";

function dismissKey(slug: string): string {
  return `${DISMISS_KEY_PREFIX}${slug}`;
}

/**
 * Session-scoped dismissal: clicking "Skip for now" suppresses the
 * overlay until the tab is closed. We use sessionStorage so the next
 * window restores the welcome card (the customer hasn't actually
 * uploaded data yet, so they still need it tomorrow).
 */
export function isOnboardingDismissed(slug: string): boolean {
  try {
    return window.sessionStorage.getItem(dismissKey(slug)) === "1";
  } catch {
    return false;
  }
}

export function markOnboardingDismissed(slug: string): void {
  try {
    window.sessionStorage.setItem(dismissKey(slug), "1");
  } catch {
    // sessionStorage disabled: caller falls back to in-memory state.
  }
}
